import { mobilePlatformForUserAgent } from './appStoreRedirect';

// Pinned so no other installed app can claim the callback.
const ANDROID_PACKAGE = 'com.olympiq';

/**
 * SECURITY: `search` and `hash` here are `location.search` / `location.hash`
 * verbatim — the hash carries the finished login's refresh token. These
 * functions only ever fold that string into the returned forwarding URL.
 * Callers must not log, fetch, or otherwise transmit it anywhere else.
 */

/** iOS (and the platform-unknown default): a plain custom-scheme URL. */
export function iosAppCallbackUrl(search: string, hash: string): string {
  return `olympiq://auth-callback${search}${hash}`;
}

/**
 * Android: the same payload via an `intent://` URL pinned to our package,
 * so a device with more than one app registered for the `olympiq` scheme
 * can't have the callback intercepted by the wrong one.
 */
export function androidAppCallbackUrl(search: string, hash: string): string {
  return `intent://auth-callback${search}${hash}#Intent;scheme=olympiq;package=${ANDROID_PACKAGE};end`;
}

/**
 * Picks the forwarding URL for the visitor's platform from a simple
 * user-agent check. Unknown/desktop user agents fall back to the plain
 * custom-scheme (iOS) form, per the product decision that any link is
 * better than none when the platform can't be identified.
 */
export function appCallbackUrlForUserAgent(
  userAgent: string,
  search: string,
  hash: string,
): string {
  return mobilePlatformForUserAgent(userAgent) === 'android'
    ? androidAppCallbackUrl(search, hash)
    : iosAppCallbackUrl(search, hash);
}

/**
 * Builds the forwarding URL and immediately navigates the browser there.
 * Returns the URL so the caller can reuse the exact same value for the
 * fallback "Open the Olympiq app" link, rather than recomputing it.
 */
export function forwardToApp(userAgent: string, search: string, hash: string): string {
  const href = appCallbackUrlForUserAgent(userAgent, search, hash);
  window.location.replace(href);
  return href;
}
